import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/api/client";
import { Store, FileText, User, Mail, Lock, Phone, MapPin, Upload, ArrowRight, Building2 } from "lucide-react";
import { toast } from "sonner";

const SellerRegister = () => {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(user ? 2 : 1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [acc, setAcc] = useState({ name: "", email: "", phone: "", password: "" });
  const [biz, setBiz] = useState({ shop_name: "", gst_number: "", owner_name: "", phone: "", address: "", city: "", document_url: "" });

  const submitAcc = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      await register({ ...acc, role: "seller" });
      setBiz((b) => ({ ...b, owner_name: acc.name, phone: acc.phone }));
      setStep(2);
    } catch (ex) { setErr(formatApiErrorDetail(ex.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const submitBiz = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      await api.post("/sellers/profile", biz);
      toast.success("Seller profile submitted! Pending verification.");
      navigate("/seller/dashboard");
    } catch (ex) { setErr(formatApiErrorDetail(ex.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="container-fah py-10 sm:py-16" data-testid="page-seller-register">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-accent text-accent-foreground rounded-full px-3 py-1.5 text-xs font-semibold mb-3">
            <Store className="w-3.5 h-3.5" /> Become a Seller
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold">Grow your agri business with FarmersAgroHub</h1>
          <p className="text-muted-foreground mt-2">Reach lakhs of farmers across India. Zero listing fee. Pay only when you sell.</p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <Step n={1} active={step >= 1} label="Account" />
          <div className={`h-px w-12 sm:w-20 ${step >= 2 ? "bg-primary" : "bg-border"}`} />
          <Step n={2} active={step >= 2} label="Business Details" />
        </div>

        {err && <div className="mb-5 text-sm bg-destructive/10 text-destructive p-3 rounded-lg max-w-2xl mx-auto">{err}</div>}

        {step === 1 && (
          <form onSubmit={submitAcc} className="card-fah p-6 sm:p-8 grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto" data-testid="form-seller-account">
            <Field icon={User} label="Owner Name" testid="seller-name" value={acc.name} onChange={(e) => setAcc({ ...acc, name: e.target.value })} required placeholder="Ramesh Patel" />
            <Field icon={Phone} label="Mobile" testid="seller-phone" type="tel" value={acc.phone} onChange={(e) => setAcc({ ...acc, phone: e.target.value })} required placeholder="+91 98765 43210" />
            <Field icon={Mail} label="Email" testid="seller-email" type="email" value={acc.email} onChange={(e) => setAcc({ ...acc, email: e.target.value })} required placeholder="you@business.com" />
            <Field icon={Lock} label="Password" testid="seller-password" type="password" value={acc.password} onChange={(e) => setAcc({ ...acc, password: e.target.value })} required placeholder="Min. 6 characters" />
            <div className="sm:col-span-2 flex items-center justify-between mt-2">
              <Link to="/login" className="text-sm text-muted-foreground hover:text-primary">Already a seller? Login</Link>
              <button disabled={loading} type="submit" className="bg-primary hover:bg-primary-hover text-white font-semibold py-2.5 px-6 rounded-lg flex items-center gap-2 disabled:opacity-50" data-testid="btn-seller-next">
                {loading ? "Please wait…" : <>Continue <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submitBiz} className="card-fah p-6 sm:p-8 grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto" data-testid="form-seller-business">
            <Field icon={Store} label="Shop / Business Name" testid="biz-shop" value={biz.shop_name} onChange={(e) => setBiz({ ...biz, shop_name: e.target.value })} required placeholder="Krishi Mart" />
            <Field icon={FileText} label="GST Number" testid="biz-gst" value={biz.gst_number} onChange={(e) => setBiz({ ...biz, gst_number: e.target.value.toUpperCase() })} required placeholder="27ABCDE1234F1Z5" maxLength={15} />
            <Field icon={User} label="Owner Name" testid="biz-owner" value={biz.owner_name} onChange={(e) => setBiz({ ...biz, owner_name: e.target.value })} required placeholder="Ramesh Patel" />
            <Field icon={Phone} label="Business Phone" testid="biz-phone" type="tel" value={biz.phone} onChange={(e) => setBiz({ ...biz, phone: e.target.value })} required />
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Business Address</label>
              <div className="mt-1 flex items-start border border-border rounded-lg bg-white focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
                <MapPin className="w-4 h-4 text-muted-foreground ml-3 mt-3" />
                <textarea required value={biz.address} onChange={(e) => setBiz({ ...biz, address: e.target.value })} rows={2} placeholder="Plot 42, Krishi Bhavan Road…" className="flex-1 px-3 py-2.5 bg-transparent focus:outline-none text-sm" data-testid="biz-address" />
              </div>
            </div>
            <Field icon={Building2} label="City" testid="biz-city" value={biz.city} onChange={(e) => setBiz({ ...biz, city: e.target.value })} required placeholder="Pune, Maharashtra" />
            <Field icon={Upload} label="Document Link (GST cert / ID)" testid="biz-doc" value={biz.document_url} onChange={(e) => setBiz({ ...biz, document_url: e.target.value })} placeholder="https://… (upload to drive & paste link)" />
            <div className="sm:col-span-2 flex items-center justify-between mt-2">
              <button type="button" onClick={() => setStep(1)} className="text-sm text-muted-foreground hover:text-primary">← Back</button>
              <button disabled={loading} type="submit" className="bg-secondary hover:opacity-90 text-secondary-foreground font-bold py-2.5 px-6 rounded-lg flex items-center gap-2 disabled:opacity-50" data-testid="btn-seller-submit">
                {loading ? "Submitting…" : <>Submit for Verification <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          </form>
        )}

        <div className="mt-10 grid sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
          {[
            { t: "0% Listing Fee", s: "Start selling for free" },
            { t: "Pan-India Reach", s: "Buyers across 18,000+ pincodes" },
            { t: "Easy Onboarding", s: "Get verified within 24-48 hrs" },
          ].map((x, i) => (
            <div key={i} className="card-fah p-4 text-center">
              <div className="font-display font-bold text-primary">{x.t}</div>
              <div className="text-xs text-muted-foreground mt-1">{x.s}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Step = ({ n, active, label }) => (
  <div className="flex items-center gap-2">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${active ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}>{n}</div>
    <span className={`text-sm font-semibold hidden sm:inline ${active ? "text-foreground" : "text-muted-foreground"}`}>{label}</span>
  </div>
);

const Field = ({ icon: Icon, label, testid, ...props }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
    <div className="mt-1 flex items-center border border-border rounded-lg bg-white focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
      <Icon className="w-4 h-4 text-muted-foreground ml-3" />
      <input data-testid={testid} {...props} className="flex-1 px-3 py-2.5 bg-transparent focus:outline-none text-sm" />
    </div>
  </div>
);

export default SellerRegister;
